import { defineComponent as _defineComponent } from 'vue'
import DictionaryService from '@/core/dictionary.service';
import CountriesService from '@/services/country.service';
import DeliveryAddressSelectItemViewObject = Vertica.LouisPoulsen.Application.Content.Pages.Basket.Business.DeliveryAddressSelectItemViewObject;
import BaseB2BBasketPageViewModel = Vertica.LouisPoulsen.Application.Content.Pages.Basket.Business.BaseB2BBasketPageViewModel;
import DeliveryCountrySelectItemViewObject = Vertica.LouisPoulsen.Application.Content.Pages.Basket.Business.DeliveryCountrySelectItemViewObject;
import UpdateDeliveryAddressWebRequest = Vertica.LouisPoulsen.Application.Commerce.Requests.Business.UpdateDeliveryAddressWebRequest;
import { store } from '@/store';
import CustomDeliveryAddressViewObject = Vertica.LouisPoulsen.Application.Commerce.ViewModels.Business.CustomDeliveryAddressViewObject;
import UpdateCustomDeliveryAddressWebRequest = Vertica.LouisPoulsen.Application.Commerce.Requests.Business.UpdateCustomDeliveryAddressWebRequest;
import { debounce } from 'lodash';
import { computed, onMounted, ref, watch } from 'vue';
import { useValidator } from '@/core/app.service';
import addressValidationService from '@/services/address-validation.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'BasketAddressSelectorB2B',
  props: {
    model: null,
    value: null,
    deliveryAddressId: { default: '' }
  },
  emits: ["update:deliveryAddressId", "input"],
  setup(__props: any, { emit }: { emit: ({(e: 'update:deliveryAddressId', id: string): void,
    (e: 'input', payload: CustomDeliveryAddressViewObject): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { model: BaseB2BBasketPageViewModel, value: CustomDeliveryAddressViewObject, deliveryAddressId: string }



const form = ref<Vertica.LouisPoulsen.Application.Commerce.ViewModels.Business.CustomDeliveryAddressViewObject>(props.value);
const states = ref<StateViewObject[]>(props.model.states);



const deliveryAddresses = ref<DeliveryAddressSelectItemViewObject[]>([]);
const selectedDeliveryAddress = ref<DeliveryAddressSelectItemViewObject>({} as DeliveryAddressSelectItemViewObject);
const selectedCustomerState = ref<StateViewObject>({} as StateViewObject);

function updateDeliveryAddress(request: UpdateDeliveryAddressWebRequest) : Promise<any> {
    return store.dispatch('basket/updateDeliveryAddress', request);
}
const debounceCustomDeliveryAddressUpdate = debounce(onCustomDeliveryAddressChanged, 750);

const phoneRegex = /^[- +0-9]*$/;
const postalCodeRegex = /^([0-9]*)([-]{1}[0-9]{4})?$/;

const dynamicAutoCompleteId = computed<string>(() => {
    return Date.now().toString();
});

const isNewAddressMode = computed<boolean>(() => {
    return selectedDeliveryAddress.value && selectedDeliveryAddress.value.id === '';
});

const countries = computed<DeliveryCountrySelectItemViewObject[]>(() => {
    return props.model.deliveryCountrySelectItems;
});

const postalRegex = computed<string | RegExp>(() => {
    return selectedCountry.value != null && selectedCountry.value.postalCodeRegex != null ? selectedCountry.value.postalCodeRegex : postalCodeRegex;
});

const selectedState = computed<StateViewObject>({
    get() {
        return states.value.find(c => c.code === form.value.regionCode);
    },
    set(newValue: StateViewObject) {
        form.value.regionCode = newValue.code;
    }
});

const selectedCountry = computed<DeliveryCountrySelectItemViewObject>({
    get() {
        return props.model.deliveryCountrySelectItems.find(c => c.countryCode === form.value.countryCode);
    },
    set(newValue: DeliveryCountrySelectItemViewObject) {
        form.value.countryCode = newValue.countryCode;
    }
});

watch(selectedCountry, onSelectedCountryChanged);
function onSelectedCountryChanged(country: DeliveryCountrySelectItemViewObject) {
    if (country && country.hasStates) {
        CountriesService.getCountryStates(country.countryCode).then(data => {
            states.value = data;

            if (data && data[0]) {
                form.value.regionCode = data[0].code;
            }
        });
    } else {
        states.value = [];
    }
    return '';
}

onMounted(() => {
    deliveryAddresses.value = buildAddressDropdown(props.model);
    selectedDeliveryAddress.value = deliveryAddresses.value.find(c => c.id === props.deliveryAddressId);

    const states = props.model.states.find(c => c.code === props.model.customDeliveryAddress.regionCode);

    if (props.model.states && props.model.states[0]) {
        form.value.regionCode = props.model.states[0].code;
    }

    selectedCustomerState.value = states;
});

function buildAddressDropdown(model: BaseB2BBasketPageViewModel): DeliveryAddressSelectItemViewObject[] {
    if (model.canShipToNewAddress) {
        const newAddressOption : DeliveryAddressSelectItemViewObject = {
            id: '',
            displayName: DictionaryService.get('Pages.CheckoutB2B.DeliveryAddressListNew')
        };

        model.deliveryAddressSelectItems.push(newAddressOption);
    }

    return model.deliveryAddressSelectItems;
}

function onDeliveryAddressChanged(address) {
    emit('update:deliveryAddressId', selectedDeliveryAddress.value.id);
    if (isNewAddressMode.value) {
        return;
    }

    const request: UpdateDeliveryAddressWebRequest = {
        basketType: props.model.basketType,
        deliveryAddressId: selectedDeliveryAddress.value.id
    };

    updateDeliveryAddress(request);
}

const $validator = useValidator();

function onCustomDeliveryAddressChanged() {
    $validator.validateAll().then((success) => {
        if (!success) {
            return;
        }
        const request: UpdateCustomDeliveryAddressWebRequest = {
            basketType: props.model.basketType,
            customDeliveryAddress: form.value
        };

        updateCustomDeliveryAddress(request);
    });
}

const addressValidationRequestId = ref<string>(null);
function updateCustomDeliveryAddress(request: UpdateCustomDeliveryAddressWebRequest) : Promise<any> {
    debounce(validateAddress, 3000)();
    return store.dispatch('basket/updateCustomDeliveryAddress', request);
}

function getErrorMessage(fieldName: string): string {
    if (fieldName) {
        return fieldName.replace(`-${dynamicAutoCompleteId.value}`, '');
    }
    return '';
}

function validateAddress() {
    const validationRequest = {
        previousResponseId: addressValidationRequestId.value,
        address: {
            city: form.value.city,
            company: '',
            countryCode: form.value.countryCode,
            district: '',
            email: '',
            name: '',
            phoneNo: '',
            postalCode: form.value.postalCode,
            regionCode: form.value.regionCode,
            street: form.value.address,
            subUnit: form.value.subUnit
        }
    };
    addressValidationService.validateAddress(validationRequest);
}

return { __sfc: true,props, form, states, emit, deliveryAddresses, selectedDeliveryAddress, selectedCustomerState, updateDeliveryAddress, debounceCustomDeliveryAddressUpdate, phoneRegex, postalCodeRegex, dynamicAutoCompleteId, isNewAddressMode, countries, postalRegex, selectedState, selectedCountry, onSelectedCountryChanged, buildAddressDropdown, onDeliveryAddressChanged, $validator, onCustomDeliveryAddressChanged, addressValidationRequestId, updateCustomDeliveryAddress, getErrorMessage, validateAddress }
}

})